import { useState } from 'react';
import { createEvent } from '../services/eventService';
import { DatabaseEvent } from '../types/types';

/**
 * Custom React hook for creating a new event.
 *
 * Handles submission state and error tracking while calling the backend API
 * to create a new event.
 *
 * @returns An object containing:
 * - createNewEvent: Async function to trigger event creation.
 * - isSubmitting: Boolean indicating if the submission is in progress.
 * - error: Any error message encountered during submission.
 */
export const useCreateEvent = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Sends a request to create a new event in the backend.
   *
   * @param eventData Partial event data to be submitted.
   */
  const createNewEvent = async (eventData: Partial<DatabaseEvent>) => {
    try {
      setIsSubmitting(true);
      await createEvent(eventData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return { createNewEvent, isSubmitting, error };
};

export default useCreateEvent;
